import * as auth from './views/common/auth.reducers';
import * as search from './views/search/search.reducers';
import * as integration from '../app/core/reducers/integration/integration.reducers';
import * as applicationSettings from '@app/views/admin/application-settings/store/application-settings.reducers';

import {
    Action,
    ActionReducer, ActionReducerMap, createFeatureSelector, createSelector,
    MetaReducer
} from '@ngrx/store';
import {environment} from '../environments/environment';
import {copyData, DataAccessRequestModel} from './core/model/integration-models';
import {Actions, AuthActionTypes} from '@app/views/common/auth.actions';
import {Preferences} from '@app/core/model/user-preferences.model';


export interface State {
    auth: auth.State;
    integration: integration.State;
    applicationSettings: applicationSettings.State;
}

export const localStorageKeys = {
    auth: 'auth',
    applicationSettings: 'applicationSettings',
    integration: 'integration'
};

export const appReducers: ActionReducerMap<State> = {
    auth: auth.authReducer,
    integration: integration.integrationReducer,
    applicationSettings: applicationSettings.applicationSettingsReducer
};

export const metaReducers: MetaReducer<State>[] = environment.production
    ? [updateStateReducer, localStorageSyncReducer]
    : [stateLogger, updateStateReducer, localStorageSyncReducer];

export function stateLogger(reducer: ActionReducer<State>): ActionReducer<State> {
    return function (state: State, action: Action): State {
        if (action.type.includes('fail') || action.type.includes('error')) {
            console.warn('state:', state, 'action:', action);
        } else {
            console.log('state:', state, 'action:', action);
        }
        return reducer(state, action);
    };
}

function assignObjectReviver(initialState: any) {
    return function (key: string, value: any): any {
        if (['loaded', 'loading', 'error'].indexOf(key) > -1) {
            return null;
        }
        if (typeof value === 'object' && initialState && initialState[key]) {
            return Object.assign(initialState[key], value);
        }
        return value;
    };
}

export function localStorageSyncReducer(reducer: ActionReducer<any>): ActionReducer<any> {
    return (state: State | undefined, action: Action) => {
        if (state === undefined) {
            const persistedAuthState = JSON.parse(localStorage.getItem(localStorageKeys.auth));
            const persistedApplicationSettingsState = JSON.parse(localStorage.getItem(localStorageKeys.applicationSettings));
            const persistedIntegrationState = JSON.parse(localStorage.getItem(localStorageKeys.integration));

            if (persistedAuthState && persistedAuthState['userPreferences']) {
                persistedAuthState.userPreferences = new Preferences(persistedAuthState.userPreferences);
            }

            const persistedState: State = {
                auth: persistedAuthState,
                applicationSettings: persistedApplicationSettingsState,
                integration: persistedIntegrationState
            };

            return persistedAuthState && persistedApplicationSettingsState && persistedIntegrationState ? persistedState : reducer(state, action);
        }

        const newState: State = reducer(state, action);

        if (action.type !== AuthActionTypes.UPDATE_STATE_FROM_LOCAL_STORAGE) {
            updateLocalStorage(newState);
        }

        return newState;
    };
}

export function updateStateReducer(reducer: ActionReducer<State>) {
    return (state: State | undefined, action: Actions) => {
        if (action.type === AuthActionTypes.UPDATE_STATE_FROM_LOCAL_STORAGE) {
            const newState = {...state};
            newState[action.payload.changedStateKey] = action.payload.changedStateValue;

            if (action.payload.changedStateKey === 'auth') {
                if (newState.auth['userPreferences']) {
                    newState.auth.userPreferences = new Preferences(newState.auth.userPreferences);
                }

                if (state?.auth?.authenticated && !newState?.auth?.authenticated) {
                    setTimeout(() =>
                        location.reload(),
                        3000
                    );
                }
            }

            return newState;
        }
        return reducer(state, action);
    };
}

export function updateLocalStorage(newState: State) {
    for (const key of Object.keys(localStorageKeys)) {
        let copeidSmallNewStateData;
        // LocalStorage has size limit to be written - 5mb. We have to reduce some data to prevent exception.
        // Clear data from third party systems: orgsMappings and courses
        if ((key === 'applicationSettings')
            && (newState[key].orgsMapping?.length > 0
                || newState[key].integration?.organizationsMappingModel
                || newState[key].qwiklabsCourses
            )) {
            copeidSmallNewStateData = copyData(newState[key]);
            if (newState[key].orgsMapping?.length > 0) {
                copeidSmallNewStateData.orgsMapping = [];
            }
            if (newState[key].integration?.organizationsMappingModel) {
                copeidSmallNewStateData.integration.organizationsMappingModel.sourceOrganizations = [];
                copeidSmallNewStateData.integration.organizationsMappingModel.targetOrganizations = [];
            }
            if (newState[key].qwiklabsCourses) {
                copeidSmallNewStateData.qwiklabsCourses = [];
            }
        }

        const newStorageState = JSON.stringify(copeidSmallNewStateData || newState[key]);

        if (localStorage.getItem(key) !== newStorageState) {
            localStorage.setItem(key, newStorageState);
        }
    }
}

export const getAuthState = createFeatureSelector<auth.State>('auth');
export const getSearchState = createFeatureSelector<search.State>('search');
export const getIntegrationState = createFeatureSelector<integration.State>('integration');

/**
 * Returns true if the user is authenticated
 * @function isAuthenticated
 * @param {State} state
 * @param {any} props
 * @return {boolean}
 */
export const isAuthenticated = createSelector(getAuthState, auth.isAuthenticated);

/**
 * Returns the authentication error.
 * @function getAuthenticationError
 * @param {State} state
 * @param {any} props
 * @return {Error}
 */
export const getAuthenticationError = createSelector(getAuthState, auth.getAuthenticationError);

/**
 * Returns true if the authentication request is loading.
 * @function isAuthenticationLoading
 * @param {State} state
 * @param {any} props
 * @return {boolean}
 */
export const isAuthenticationLoading = createSelector(getAuthState, auth.isLoading);

/**
 * Returns authenticated user.
 * @function getAuthenticatedUser
 * @param {State} state
 * @param {any} props
 * @returns {User}
 */
export const getAuthenticatedUser = createSelector(getAuthState, auth.getAuthenticatedUser);

/**
 * Returns true if the content is visible.
 * @function isVisibleContent
 * @param {State} state
 * @returns {boolean}
 */
export const isVisibleContent = createSelector(getAuthState, auth.isVisibleContent);


/**
 * Returns true if the navigation menu is opened.
 * @function isMenuOpened
 * @param {State} state
 * @returns {boolean}
 */
export const isMenuOpened = createSelector(getAuthState, auth.isMenuOpened);

/**
 * Returns context district.
 * @function getContextDistrict
 * @param {State} state
 * @param {any} props
 * @returns {any}
 */
// export const getContextDistrict = createSelector(getSearchState, search.getContextDistrict);

/**
 * Returns global search filter id.
 * @function getGlobSearchFilter
 * @param {State} state
 * @param {any} props
 * @returns {any}
 */
// xport const getGlobSearchFilter = createSelector(getSearchState, search.getGlobSearchFilter);

/**
 * Returns authenticated user preferences.
 * @function getAuthenticatedUserPreferences
 * @param {State} state
 * @returns {User}
 */
export const getAuthenticatedUserPreferences = createSelector(getAuthState, auth.getAuthenticatedUserPreferences);

/**
 * Returns authenticated user preferences.
 * @function getAuthenticatedUserPreferences
 * @param {State} state
 * @returns {User}
 */
export const getAuthenticatedUserApplications = createSelector(getAuthState, auth.getAuthenticatedUserApplications);

/**
 * Returns SignOut redirectUrl
 * @function getSignOutRedirectUrl
 * @param {State} state
 * @param {any} props
 * @return {string}
 */
export const getSignOutRedirectUrl = createSelector(getAuthState, auth.getSignOutRedirectUrl);

/**
 * Returns User Organization
 * @function getUserOrganization
 * @param {State} state
 * @param {any} props
 * @return {string}
 */
export const getUserOrganization = createSelector(getAuthState, auth.getUserOrganization);

/**
 * Returns default integration.
 * @function getDefaultIntegration
 * @param {State} state
 * @returns {IntegrationModel}
 */
export const getDefaultIntegration = createSelector(getIntegrationState, integration.getDefaultIntegration);

/**
 * Returns default schema.
 * @function getDefaultSchema
 * @param {State} state
 * @returns {AttributeMetadataModel[]}
 */
export const getDefaultSchema = createSelector(getIntegrationState, integration.getDefaultSchema);

/**
 * Returns default categories.
 * @function getDefaultSchema
 * @param {State} state
 * @returns {AttributeMetadataModel[]}
 */
export const getDefaultCategories = createSelector(getIntegrationState, integration.getDefaultCategories);

/**
 * Returns if get  default categories failed.
 * @function getDefaultCategoriesFailed
 * @param {State} state
 * @returns {Error}
 */
export const getDefaultCategoriesFailed = createSelector(getIntegrationState, integration.getDefaultCategoriesFailed);

/**
 *
 * Returns School Year Rollover Settings.
 * @function getSchoolYearRolloverSettings
 * @param {State} state
 * @returns {ISchoolYearRolloverSettings}
 */
export const getSchoolYearRolloverSettings = createSelector(getIntegrationState, integration.getSchoolYearRolloverSettings);

/**
 * Returns pending requests of all Vendor's applications.
 * @function getAllPendingRequests
 * @param {State} state
 * @returns {DataAccessRequestModel[]}
 */
export const getAllPendingRequests = createSelector(getIntegrationState, integration.getAllPendingRequests);

/**
 * Returns pending requests.
 * @function getPendingRequests
 * @param {State} state
 * @returns {DataAccessRequestModel[]}
 */
export const getPendingRequests = createSelector(getIntegrationState, integration.getPendingRequests);

/**
 * Returns provider applications.
 * @function getProviderApplications
 * @param {State} state
 * @returns {ApplicationProviderListModel[]}
 */
export const getProviderApplications = createSelector(getIntegrationState, integration.getProviderApplications);

/**
 * Returns if get provider applications failed.
 * @function getProviderApplicationsFailed
 * @param {State} state
 * @returns {Error}
 */
export const getProviderApplicationsFailed = createSelector(getIntegrationState, integration.getProviderApplicationsFailed);

/**
 * Returns provider applications.
 * @function getProviderApplications
 * @param {State} state
 * @returns {ApplicationProviderListModel[]}
 */
export const getDeletingApplications = createSelector(getIntegrationState, integration.getDeletingApplications);

/**
 * Returns vendor institutions.
 * @function getVendorInstitutions
 * @param {State} state
 * @returns {InstitutionsVendorListModel[]}
 */
export const getVendorInstitutions = createSelector(getIntegrationState, integration.getVendorInstitutions);

/**
 * Returns if provider vendor institutions failed.
 * @function getVendorInstitutionsFailed
 * @param {State} state
 * @returns {Error}
 */
export const getVendorInstitutionsFailed = createSelector(getIntegrationState, integration.getVendorInstitutionsFailed);

/**
 * Returns all vendor applications.
 * @function getAllVendorApplications
 * @param {State} state
 * @returns {InstitutionsVendorListModel[]}
 */
export const getAllVendorApplications = createSelector(getIntegrationState, integration.getAllVendorApplications);

/**
 * Returns if getting all vendor applications failed.
 * @function getAllVendorApplicationsFailed
 * @param {State} state
 * @returns {Error}
 */
export const getAllVendorApplicationsFailed = createSelector(getIntegrationState, integration.getAllVendorApplicationsFailed);

/**
 *
 * Returns org Feature toggles.
 * @function getFeatureToggles
 * @param {State} state
 * @returns {FeatureToggleModel[]}
 */
export const getFeatureToggles = createSelector(getAuthState, auth.featureToggles);

/**
 *
 * Returns System PII levels config.
 * @function getPIIShieldLevelsConfig
 * @param {State} state
 * @returns {PiiShieldLevelConfig[]}
 */
export const getPIIShieldLevelsConfig = createSelector(getAuthState, auth.getPIIShieldLevelsConfig);

/**
 * Returns Passport Login IDP list.
 * @function getPassportLoginIDPs
 * @param {State} state
 * @returns {PassportLoginIDP[]}
 */
export const getPassportLoginIDPs = createSelector(getAuthState, auth.getPassportLoginIDPs);

/**
 * Returns Onboarding Wizard Completed Steps.
 * @function getOnboardingWizardCompletedSteps
 * @param {State} state
 * @returns {OnboardingWizardStep[]}
 */
export const getOnboardingWizardCompletedSteps = createSelector(getAuthState, auth.getOnboardingWizardCompletedSteps);
